import * as React from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography } from "@mui/material";
import { Close, FolderOpenOutlined, ImageOutlined } from '@mui/icons-material';
import FileManager, { FileManagerProps } from "./FileManager";
import { DialogProps } from '@mui/material/Dialog';

interface ExtraProps {
  assets: boolean;
  contextLabel?: string;
}

export class FileManagerDialog extends React.Component<FileManagerProps & DialogProps & ExtraProps, {}>{
  render() {

    const { getItems, deleteItem, uploadSingleFile, createFolder, onPick, getStaticUrl, assets, renamePath, transformImage, contextLabel, ...dialogProps } = this.props;

    const heading = assets ? 'Website Assets' : 'Website Static Files';
    const subtitle = assets
      ? 'Theme assets bundled with the build pipeline (CSS, JS, fonts, source images).'
      : 'Files served directly under the site root (images, downloads, uploads).';
    const HeaderIcon = assets ? ImageOutlined : FolderOpenOutlined;

    const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
      // 'closeButton' is not a standard MUI reason ('backdropClick' | 'escapeKeyDown'),
      // but the upstream consumer's onClose ignores the reason — pass through unchanged.
      dialogProps.onClose?.(event, 'escapeKeyDown');
    };

    return (
      <Dialog
        {...dialogProps}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { overflow: 'hidden' } } }}
      >
        <Box sx={{
          position: 'relative',
          padding: { xs: '20px 20px 16px', sm: '24px 28px 20px' },
          background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.06) 0%, rgba(5, 35, 114, 0.0) 100%)',
          borderBottom: '1px solid rgba(11, 18, 48, 0.08)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.10) 0%, rgba(5, 35, 114, 0.04) 100%)',
              border: '1px solid rgba(5, 35, 114, 0.14)',
              color: '#052372',
            }} aria-hidden>
              <HeaderIcon fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {contextLabel && (
                <Typography variant="overline" sx={{ display: 'block', color: 'rgba(11, 18, 48, 0.55)', lineHeight: 1.4 }}>
                  {contextLabel}
                </Typography>
              )}
              <Typography
                id="file-manager-dialog"
                component="h2"
                sx={{
                  fontFamily: '"Inter Tight", "Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: 20, sm: 22 },
                  letterSpacing: '-0.012em',
                  color: '#0b1230',
                  lineHeight: 1.2,
                }}
              >
                {heading} Manager
              </Typography>
              <Typography sx={{
                mt: 0.5,
                fontSize: 13,
                lineHeight: 1.5,
                color: 'rgba(11, 18, 48, 0.62)',
                maxWidth: 540,
              }}>
                {subtitle}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="Close"
              sx={{
                color: 'rgba(11, 18, 48, 0.55)',
                '&:hover': { color: '#0b1230', backgroundColor: 'rgba(11, 18, 48, 0.06)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ padding: { xs: '16px 20px 24px', sm: '20px 28px 28px' } }}>
          <FileManager
            transformImage={transformImage}
            renamePath={renamePath}
            getItems={getItems}
            deleteItem={deleteItem}
            uploadSingleFile={uploadSingleFile}
            createFolder={createFolder}
            onPick={onPick}
            getStaticUrl={getStaticUrl}
          />
        </DialogContent>
      </Dialog>
    );
  }
}
